/**
 * /cells/:id — one cell's definition, editable.
 *
 * Phase A: rich numeric form + live 3D preview. Every field change
 * updates the preview immediately so the operator sees the geometry
 * they're configuring. The preview reuses the main viewport's Scene
 * class, so the positioner geometry here matches what the project
 * viewport will show.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Button,
  Callout,
  Card,
  FormGroup,
  HTMLSelect,
  HTMLTable,
  NumericInput,
  Spinner,
  Switch,
  Tag,
} from '@blueprintjs/core'

import {
  deleteCell,
  getCell,
  listTools,
  updateCell,
  type CellDefinition,
} from '../api'
import type { ToolDefinition } from '../store'
import { Scene, type ViewportCell } from '../viewport/Scene'

const ROBOT_OPTIONS = [
  { value: 'ABB_IRB4600_40_255', label: 'ABB IRB 4600-40/2.55 (default)' },
]

const POSITIONER_PRESETS: Record<string, {
  label: string
  first_axis_in_base: [number, number, number]
  faceplate_offset_mm: [number, number, number]
}> = {
  IRBP_A250_D1000: {
    label: 'Wheel positioner — horizontal shaft (A-250 style)',
    first_axis_in_base: [1, 0, 0],
    faceplate_offset_mm: [0, 0, 1100],
  },
  LAZY_SUSAN: {
    label: 'Lazy Susan — vertical shaft',
    first_axis_in_base: [0, 0, 1],
    faceplate_offset_mm: [0, 0, 500],
  },
  CUSTOM: {
    label: 'Custom',
    first_axis_in_base: [1, 0, 0],
    faceplate_offset_mm: [0, 0, 1100],
  },
}

export function CellDetail() {
  const { id } = useParams<{ id: string }>()
  const [cell, setCell] = useState<CellDefinition | null>(null)
  const [tools, setTools] = useState<ToolDefinition[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const previewHost = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<Scene | null>(null)

  const refresh = useCallback(async () => {
    if (!id) return
    try {
      setCell(await getCell(id))
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    }
  }, [id])
  useEffect(() => { refresh() }, [refresh])
  useEffect(() => { listTools().then(setTools).catch(() => {}) }, [])

  // Mount the preview Scene once the cell has loaded (not before — the
  // preview div is behind a loading <Spinner /> until then, so an
  // on-mount effect would see previewHost.current = null and bail).
  // `skipRobotMesh` avoids pulling the seven IRB 4600 STLs here —
  // this preview is about the positioner geometry the user is editing,
  // and loading the robot meshes on both this page AND the main
  // viewport simultaneously trips Chrome's per-origin fetch cap
  // (ERR_INSUFFICIENT_RESOURCES).
  const cellLoaded = cell != null
  useEffect(() => {
    if (!cellLoaded || !previewHost.current) return
    const s = new Scene(previewHost.current, { skipRobotMesh: true })
    sceneRef.current = s
    s.start()
    return () => {
      s.dispose()
      sceneRef.current = null
    }
  }, [cellLoaded])

  // Push every cell edit into the preview. First time (freshly loaded
  // cell) we also fit the camera to the positioner — the Scene's
  // default camera is sized for a robot-included view, which puts the
  // positioner-only preview way off-centre.
  const didInitialFit = useRef(false)
  useEffect(() => {
    if (!cell || !sceneRef.current) return
    const vp: ViewportCell = {
      robot: { position_mm: cell.robot.position_mm },
      positioner: {
        base_position_mm: cell.positioner.base_position_mm,
        base_orientation_deg: cell.positioner.base_orientation_deg,
        first_axis_in_base: cell.positioner.first_axis_in_base,
        faceplate_offset_mm: cell.positioner.faceplate_offset_mm,
        // Phase 2D: pass the tilt fields through for the viewport mesh.
        // ``tilt_mech_unit_name`` empty = Scene skips the tilt entirely
        // (same as pre-Phase-2D behaviour).
        tilt_mech_unit_name: (cell.positioner as any).tilt_mech_unit_name,
        tilt_axis_in_base: (cell.positioner as any).tilt_axis_in_base,
        tilt_angle_deg: (cell.positioner as any).tilt_angle_deg,
      },
      work_object: {
        offset_mm: cell.work_object.offset_mm,
        worm_axis_in_faceplate: cell.work_object.worm_axis_in_faceplate,
      },
    }
    sceneRef.current.setCell(vp)
    if (!didInitialFit.current) {
      sceneRef.current.zoomToFit()
      didInitialFit.current = true
    }
  }, [cell])

  if (err) return <div className="projects-root"><Callout intent="danger" icon="error">{err}</Callout></div>
  if (!cell) return <div className="projects-root"><Spinner /></div>

  const save = async () => {
    setSaving(true)
    try {
      const updated = await updateCell(cell)
      setCell(updated)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    if (!confirm(`Delete cell "${cell.name}"? Projects referencing it fall back to demo_cell.`)) return
    await deleteCell(cell.id)
    window.location.href = '/cells'
  }

  const applyPositionerPreset = (preset_id: string) => {
    const p = POSITIONER_PRESETS[preset_id]
    if (!p) return
    setCell({
      ...cell,
      positioner: {
        ...cell.positioner,
        model_id: preset_id,
        first_axis_in_base: [...p.first_axis_in_base],
        faceplate_offset_mm: [...p.faceplate_offset_mm],
      },
    })
  }

  return (
    <div className="projects-root cell-detail-root">
      <header className="projects-header">
        <Link to="/cells" className="projects-back">← Cells</Link>
        <h2>{cell.name}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Tag minimal>v{cell.version}</Tag>
          <Tag minimal>{cell.robot.robot_id}</Tag>
          <Tag minimal>{cell.positioner.model_id}</Tag>
          <Button icon="floppy-disk" intent="primary" onClick={save} loading={saving}>Save</Button>
          <Button icon="trash" minimal intent="danger" onClick={onDelete}>Delete</Button>
        </div>
      </header>

      <div className="cell-detail-grid">
        <div className="cell-detail-form">

      <Card>
        <h3>Robot</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <FormGroup label="Robot model" helperText="Picks the analytical IK + the mesh set.">
            <HTMLSelect fill
              value={cell.robot.robot_id}
              onChange={(e) => setCell({ ...cell, robot: { ...cell.robot, robot_id: e.target.value } })}
              options={ROBOT_OPTIONS}
            />
          </FormGroup>
          <FormGroup label="Tool length (m)" helperText="Flange face to wire tip. Used in IK to project the TCP.">
            <NumericInput value={cell.robot.tool_length_m} stepSize={0.01} minorStepSize={0.001} fill
              onValueChange={(v) => setCell({ ...cell, robot: { ...cell.robot, tool_length_m: v } })} />
          </FormGroup>
          <div />
          <Vec3Input label="Base position (mm)" vec={cell.robot.position_mm}
            helperText="Where the robot base sits in the station's world frame. Usually (0, 0, 0)."
            onChange={(v) => setCell({ ...cell, robot: { ...cell.robot, position_mm: v } })} />
          <Vec3Input label="Base orientation (deg)" vec={cell.robot.orientation_deg}
            helperText="XYZ intrinsic Euler. Leave at (0, 0, 0) unless the base is tilted."
            onChange={(v) => setCell({ ...cell, robot: { ...cell.robot, orientation_deg: v } })} />
        </div>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <h3>Positioner</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FormGroup label="Preset" helperText="Snaps the shaft axis and faceplate offset to a common layout. 'Custom' leaves them untouched.">
            <HTMLSelect fill
              value={POSITIONER_PRESETS[cell.positioner.model_id] ? cell.positioner.model_id : 'CUSTOM'}
              onChange={(e) => applyPositionerPreset(e.target.value)}
              options={Object.entries(POSITIONER_PRESETS).map(([v, p]) => ({ value: v, label: p.label }))}
            />
          </FormGroup>
          <FormGroup label="Mech unit name" helperText="Must match the external-axis task in your RobotStudio VC (e.g. STN1).">
            <input className="bp5-input bp5-fill" value={cell.positioner.mech_unit_name}
              onChange={(e) => setCell({ ...cell, positioner: { ...cell.positioner, mech_unit_name: e.target.value } })} />
          </FormGroup>
          <Vec3Input label="Base position (mm, in robot RCF)" vec={cell.positioner.base_position_mm}
            helperText="Where the positioner base sits relative to the robot base. Typical: ~1.6 m in front."
            onChange={(v) => setCell({ ...cell, positioner: { ...cell.positioner, base_position_mm: v } })} />
          <Vec3Input label="Base orientation (deg)" vec={cell.positioner.base_orientation_deg}
            helperText="XYZ intrinsic Euler. 90° yaw rotates the shaft from robot +X to +Y."
            onChange={(v) => setCell({ ...cell, positioner: { ...cell.positioner, base_orientation_deg: v } })} />
          <Vec3Input label="First axis (in base frame)" vec={cell.positioner.first_axis_in_base}
            helperText="Direction of the first rotary axis. (1, 0, 0) horizontal wheel; (0, 0, 1) lazy susan."
            onChange={(v) => setCell({ ...cell, positioner: { ...cell.positioner, first_axis_in_base: v } })} />
          <Vec3Input label="Faceplate offset (mm, in base frame)" vec={cell.positioner.faceplate_offset_mm}
            helperText="Faceplate centre relative to positioner base. Typical: (0, 0, 1100) for an A-250 D1000."
            onChange={(v) => setCell({ ...cell, positioner: { ...cell.positioner, faceplate_offset_mm: v } })} />
          <FormGroup label="RAPID external axis"
            helperText="On = emit ActUnit/DeactUnit and wire the positioner as an external axis in wobjdata. Off for fresh VCs where the mech unit isn't configured yet.">
            <Switch
              checked={cell.positioner.activate_in_proc}
              label={cell.positioner.activate_in_proc ? 'Active' : 'Inactive'}
              onChange={(e) => setCell({ ...cell, positioner: { ...cell.positioner, activate_in_proc: e.currentTarget.checked } })}
            />
          </FormGroup>
        </div>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <h3>Work object</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Vec3Input label="Offset on faceplate (mm)" vec={cell.work_object.offset_mm}
            helperText="Centre of the welded part relative to the faceplate centre."
            onChange={(v) => setCell({ ...cell, work_object: { ...cell.work_object, offset_mm: v } })} />
          <Vec3Input label="Orientation (deg)" vec={cell.work_object.orientation_deg}
            helperText="Rotation of the part about its own origin on the faceplate."
            onChange={(v) => setCell({ ...cell, work_object: { ...cell.work_object, orientation_deg: v } })} />
          <Vec3Input label="Worm axis (in faceplate frame)" vec={cell.work_object.worm_axis_in_faceplate}
            helperText="Direction of the worm shaft on the faceplate. (0, 0, 1) = along the faceplate normal."
            onChange={(v) => setCell({ ...cell, work_object: { ...cell.work_object, worm_axis_in_faceplate: v } })} />
        </div>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <h3>Active torch</h3>
        <FormGroup label="Tool"
          helperText="Projects without their own torch fall back to this cell's torch. Pick from the tool library.">
          <HTMLSelect fill
            value={cell.active_tool_id ?? ''}
            onChange={(e) => setCell({ ...cell, active_tool_id: e.target.value || null })}
            options={[{ value: '', label: '(none)' }, ...tools.map((t) => ({ value: t.id, label: `${t.vendor} ${t.part} — ${t.id}` }))]}
          />
        </FormGroup>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <h3>Computed worm origin</h3>
        <HTMLTable compact>
          <tbody>
            <tr><td>Worm origin in RCF (mm)</td>
              <td><code>
                ({cell.positioner.base_position_mm[0] + cell.positioner.faceplate_offset_mm[0] + cell.work_object.offset_mm[0] - cell.robot.position_mm[0]},{' '}
                {cell.positioner.base_position_mm[1] + cell.positioner.faceplate_offset_mm[1] + cell.work_object.offset_mm[1] - cell.robot.position_mm[1]},{' '}
                {cell.positioner.base_position_mm[2] + cell.positioner.faceplate_offset_mm[2] + cell.work_object.offset_mm[2] - cell.robot.position_mm[2]})
              </code></td>
            </tr>
          </tbody>
        </HTMLTable>
      </Card>
        </div>

        <div className="cell-detail-preview">
          <div className="cell-detail-preview-label">Live preview</div>
          <div ref={previewHost} className="cell-detail-preview-canvas" />
          <div className="cell-detail-preview-hint">
            Updates on every field change. The positioner geometry here matches
            what the pipeline will see.
          </div>
        </div>
      </div>
    </div>
  )
}

function Vec3Input({
  label, vec, helperText, onChange,
}: {
  label: string
  vec: number[]
  helperText?: string
  onChange: (v: number[]) => void
}) {
  return (
    <FormGroup label={label} helperText={helperText}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0, 1, 2].map((i) => (
          <NumericInput key={i} value={vec[i] ?? 0} stepSize={10}
            onValueChange={(v) => {
              const next = [...vec]
              next[i] = v
              onChange(next)
            }} />
        ))}
      </div>
    </FormGroup>
  )
}
